/**
 * PATH: packages/ui/src/shortcuts/ShortcutProvider.tsx
 * FILE_ID: UI-SHORTCUTS-PROVIDER-001
 * PURPOSE: The central keyboard dispatcher as a React provider + hooks.
 *          Two document keydown listeners (capture + bubble phase) resolve the
 *          active scope and fire the highest-precedence REGISTERED handler.
 *          Capture-phase actions (e.g. ⌘F) are resolved before browser defaults
 *          and stopPropagation on match; every other action stays bubble-phase.
 *          Hubs call useShortcut() to bind behavior to action ids and
 *          useShortcutScope() to declare the current hub/screen.
 * WHY: Replaces scattered per-hub window keydown listeners with a single,
 *      precedence-aware, guard-aware dispatcher. During the expand phase it
 *      ONLY acts on explicitly-registered actions, so un-migrated hubs keep
 *      working (it never swallows keys it has no handler for).
 *
 * FLOW:
 *   useShortcut(id, fn) ─► handlers ─┐
 *   useShortcutScope(ctx) ─► scope ──┼─► keydown → resolveShortcut → fn() + preventDefault
 *   <ShortcutProvider overrides> ────┘
 *
 * RELATED:
 *   - ./dispatcher.ts: resolveShortcut (pure)
 *   - ./actions.ts: SHORTCUT_ACTIONS
 *   - ../lib/utils: isMac (platform detection)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { isMac } from '../lib/utils'
import { resolveShortcut } from './dispatcher'
import { SHORTCUT_ACTIONS } from './actions'
import type {
  Platform,
  ShortcutAction,
  ShortcutContext,
  ShortcutOverrideMap,
} from './types'

interface RegisteredHandler {
  run: (event: KeyboardEvent) => void
  when?: () => boolean
}

interface ScopeEntry {
  token: number
  ctx: ShortcutContext
}

interface ShortcutContextValue {
  platform: Platform
  overrides: ShortcutOverrideMap
  actions: ShortcutAction[]
  /** Effective route context (latest hub + latest screen across mounted scopes). */
  getContext: () => ShortcutContext
  register: (id: string, handler: RegisteredHandler) => () => void
  pushScope: (ctx: ShortcutContext) => () => void
}

const Ctx = createContext<ShortcutContextValue | null>(null)

export interface ShortcutProviderProps {
  /** User customizations (from useShortcutOverrides). Defaults to none. */
  overrides?: ShortcutOverrideMap
  /** Override platform detection (tests). Defaults to isMac() ? 'mac':'other'. */
  platform?: Platform
  /** Full descriptor set. Defaults to the built-in SHORTCUT_ACTIONS. */
  actions?: ShortcutAction[]
  children: ReactNode
}

export function ShortcutProvider({
  overrides = {},
  platform,
  actions = SHORTCUT_ACTIONS,
  children,
}: ShortcutProviderProps) {
  const resolvedPlatform: Platform = platform ?? (isMac() ? 'mac' : 'other')

  const handlersRef = useRef<Map<string, RegisteredHandler>>(new Map())
  const scopesRef = useRef<ScopeEntry[]>([])
  // Keep the latest props readable inside the stable keydown listener.
  const overridesRef = useRef(overrides)
  const actionsRef = useRef(actions)
  const platformRef = useRef(resolvedPlatform)
  overridesRef.current = overrides
  actionsRef.current = actions
  platformRef.current = resolvedPlatform

  const getContext = useCallback((): ShortcutContext => {
    const ctx: ShortcutContext = {}
    for (const entry of scopesRef.current) {
      if (entry.ctx.hub !== undefined) ctx.hub = entry.ctx.hub
      if (entry.ctx.screen !== undefined) ctx.screen = entry.ctx.screen
    }
    return ctx
  }, [])

  const register = useCallback((id: string, handler: RegisteredHandler) => {
    handlersRef.current.set(id, handler)
    return () => {
      // Only delete if still the same handler (guards fast re-registers).
      if (handlersRef.current.get(id) === handler) handlersRef.current.delete(id)
    }
  }, [])

  const pushScope = useCallback((ctx: ShortcutContext) => {
    const entry: ScopeEntry = { token: Math.random(), ctx }
    scopesRef.current.push(entry)
    return () => {
      scopesRef.current = scopesRef.current.filter((e) => e.token !== entry.token)
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return
    // One dispatch routine, run for both phases. Each action is resolved in
    // EXACTLY ONE phase — capture-flagged actions on the capture listener, all
    // others on bubble — so a chord can never double-fire even though two
    // document listeners are attached.
    const dispatch = (event: KeyboardEvent, phase: 'capture' | 'bubble') => {
      const handlers = handlersRef.current
      if (handlers.size === 0) return
      const wantCapture = phase === 'capture'
      // Only consider actions that have a live, currently-enabled handler AND
      // belong to this phase, so the provider never swallows keys still owned
      // by legacy hub handlers and never handles one action twice.
      const candidates = actionsRef.current.filter((a) => {
        if ((a.capture === true) !== wantCapture) return false
        const h = handlers.get(a.id)
        return !!h && (!h.when || h.when())
      })
      if (candidates.length === 0) return

      const winner = resolveShortcut({
        actions: candidates,
        overrides: overridesRef.current,
        context: getContext(),
        event,
        platform: platformRef.current,
        target: event.target,
      })
      if (!winner) return
      const handler = handlers.get(winner.id)
      if (!handler) return
      event.preventDefault()
      // Capture-phase actions are the single authority for their chord: stop the
      // event reaching browser defaults / downstream hub listeners.
      if (winner.capture) event.stopPropagation()
      handler.run(event)
    }
    const onCapture = (event: KeyboardEvent) => dispatch(event, 'capture')
    const onBubble = (event: KeyboardEvent) => dispatch(event, 'bubble')
    document.addEventListener('keydown', onCapture, { capture: true })
    document.addEventListener('keydown', onBubble)
    return () => {
      document.removeEventListener('keydown', onCapture, { capture: true })
      document.removeEventListener('keydown', onBubble)
    }
  }, [getContext])

  const value = useMemo<ShortcutContextValue>(
    () => ({
      platform: resolvedPlatform,
      overrides,
      actions,
      getContext,
      register,
      pushScope,
    }),
    [resolvedPlatform, overrides, actions, getContext, register, pushScope],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** Access the provider value (registry/overrides/platform). For help/editor UIs. */
export function useShortcutRegistry(): ShortcutContextValue | null {
  return useContext(Ctx)
}

export interface UseShortcutOptions {
  /** Skip while false (e.g. modal closed). Re-registers when it flips true. */
  enabled?: boolean
  /** Evaluated at fire time; return false to let the event fall through. */
  when?: () => boolean
}

/**
 * useShortcut — bind a handler to an action id. No-op outside a provider (so a
 * hub can call it unconditionally). Re-registers when handler/enabled change.
 */
export function useShortcut(
  id: string,
  handler: (event: KeyboardEvent) => void,
  options: UseShortcutOptions = {},
): void {
  const ctx = useContext(Ctx)
  const handlerRef = useRef(handler)
  handlerRef.current = handler
  const whenRef = useRef(options.when)
  whenRef.current = options.when
  const { enabled = true } = options

  useEffect(() => {
    if (!ctx || !enabled) return
    return ctx.register(id, {
      run: (event) => handlerRef.current(event),
      when: () => (whenRef.current ? whenRef.current() : true),
    })
  }, [ctx, id, enabled])
}

/**
 * useShortcutScope — declare the current hub/screen while this component is
 * mounted. Nested scopes stack; the latest hub/screen wins (screen > hub at
 * dispatch). No-op outside a provider.
 */
export function useShortcutScope(scope: ShortcutContext): void {
  const ctx = useContext(Ctx)
  const hub = scope.hub
  const screen = scope.screen
  useEffect(() => {
    if (!ctx) return
    return ctx.pushScope({ hub, screen })
  }, [ctx, hub, screen])
}
